import React, { useState } from 'react';
import DocumentForm from './DocumentForm';
import DocumentFormWithPricing from './DocumentFormWithPricing';
import DocumentEdit from './DocumentEdit';
import DocumentsModule from './DocumentsModule';
import ActivityTimeline from './ActivityTimeline';
import PaymentManager, { PaymentStatus } from './PaymentManager';

const DocumentsManager = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [exportLoading, setExportLoading] = useState(false);
  const [showPaymentManager, setShowPaymentManager] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handlePaymentUpdate = (updatedDocument) => {
    setSelectedDocument(updatedDocument);
    setRefreshKey(prev => prev + 1); // Force refresh du module documents
  };

  const openPaymentManager = (document) => {
    setSelectedDocument(document);
    setShowPaymentManager(true);
  };

  const closePaymentManager = () => {
    setShowPaymentManager(false);
    setSelectedDocument(null);
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setActiveTab('edit');
  };

  const handleSaveDocument = (updatedDocument) => {
    setEditingDocument(null);
    setActiveTab('list');
    setRefreshKey(prev => prev + 1); // Force refresh
  };

  const handleBackFromEdit = () => {
    setEditingDocument(null);
    setActiveTab('list');
  };

  const handleStatusChange = (documentId, newStatus) => {
    setRefreshKey(prev => prev + 1); // Force refresh
  };

  const handleDeleteDocument = (documentId) => {
    setRefreshKey(prev => prev + 1); // Force refresh after deletion
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/documents/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'documents_export.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de l\'export CSV');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/documents/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'documents_export.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de l\'export PDF');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'export PDF');
    } finally {
      setExportLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'create':
        return (
          <DocumentFormWithPricing 
            onSubmit={async (documentData) => {
              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  },
                  body: JSON.stringify(documentData)
                });
                
                if (response.ok) {
                  alert('Document créé avec succès !');
                  setActiveTab('list');
                  setRefreshKey(prev => prev + 1);
                } else {
                  alert('Erreur lors de la création du document');
                }
              } catch (error) {
                console.error('Erreur:', error);
                alert('Erreur lors de la création du document');
              }
            }}
            onCancel={() => setActiveTab('list')}
          />
        );
      case 'edit':
        return editingDocument ? (
          <DocumentEdit 
            document={editingDocument}
            onBack={handleBackFromEdit}
            onSave={handleSaveDocument}
          />
        ) : (
          <div className="text-center py-8">
            <p>Aucun document sélectionné pour modification</p>
            <button onClick={() => setActiveTab('list')} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg">
              Retour à la liste
            </button>
          </div>
        );
      case 'activity':
        return <ActivityTimeline documentType="devis" showFilters={true} maxItems={100} />;
      case 'list':
      default:
        return (
          <DocumentsModule 
            key={refreshKey}
            onOpenPaymentManager={openPaymentManager}
            onEditDocument={handleEditDocument}
            onStatusChange={handleStatusChange}
            onDeleteDocument={handleDeleteDocument}
          />
        );
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Documents</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'list' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📋 Liste des Documents
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'create' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ➕ Créer un Document
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-lg ${
              activeTab === 'activity' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📊 Historique
          </button>
          
          {/* Export buttons */}
          <div className="flex items-center gap-2 ml-4 border-l pl-4">
            <button
              onClick={handleExportCSV}
              disabled={exportLoading}
              className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Export CSV
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exportLoading}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {exportLoading ? '⏳' : '📄'} Export PDF
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        {renderContent()}
      </div>
      
      {/* Payment Manager Modal */}
      {showPaymentManager && selectedDocument && (
        <PaymentManager
          document={selectedDocument}
          onPaymentUpdate={handlePaymentUpdate}
          onClose={closePaymentManager}
        />
      )}
    </div>
  );
};

export default DocumentsManager;